/**
 * Helpers del visor de presentaciones HTML por módulo.
 *
 * Cada módulo de un curso tiene un `topic_id` que apunta a un
 * `template_topic`. Ese topic tiene un `html_ref` (path al HTML del recorte
 * deduplicado, ej. "U1_C1/index.html") y un `status`.
 *
 * El HTML del recorte puede tener placeholders tipo {{TITULO_CLASE}} que
 * se reemplazan en runtime con datos del curso/módulo, así una misma
 * "plantilla de portada" sirve para las 23 clases sin regenerar HTML.
 *
 * Storage:
 *   - Dev:  http://127.0.0.1:8765 (server local de HTMLs deduplicados)
 *   - Prod: cuando se migre — CDN (R2 / Supabase Storage)
 *   Configurable vía VITE_RECORTES_HTML_BASE en .env.local
 */
import { supabase } from './supabase';
import type { TemplateTopic, TemplateSubtopic } from '../types/templates';


/** Cache en memoria: moduleId → TemplateTopic. Dura mientras la pestaña está abierta. */
const topicCache = new Map<string, TemplateTopic | null>();

/**
 * Cache de slides por URL de recorte.
 * key: URL del index.html del recorte
 * value: array de URLs absolutas a los slides individuales
 * Dura mientras la pestaña esté abierta → segunda apertura de cualquier módulo es instantánea.
 */
export const slidesCache = new Map<string, string[]>();

/** Base URL donde se sirven los HTMLs.
 *  - Dev local  → http://127.0.0.1:8765
 *  - Producción → proxy /api/guide (Vercel → Supabase Storage guides-html)
 */
const _envBase = (import.meta.env.VITE_RECORTES_HTML_BASE as string | undefined)?.replace(/\/$/, '');
const _isLocal = typeof window !== 'undefined'
  ? (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  : true;
// En prod, usar siempre el mismo origen de la página para evitar CORS cross-domain.
const _prodBase = typeof window !== 'undefined' && !_isLocal
  ? `${window.location.protocol}//${window.location.host}/api/guide`
  : null;
export const RECORTES_HTML_BASE: string =
  _envBase ?? (_isLocal ? 'http://127.0.0.1:8765' : (_prodBase ?? 'https://cubus-intranet.vercel.app/api/guide'));


/**
 * Plantilla universal: portada + índice con placeholders. Sirve a TODA
 * clase que no tenga aún su HTML propio. Vive en _TEMPLATE/.
 *
 * Cuando el visor se topa con un topic sin html_ref ni fallback, sirve
 * esta plantilla con los datos del módulo (título, número, subtopics
 * convertidos en cards) inyectados.
 */
export const PLANTILLA_UNIVERSAL = '_TEMPLATE/presentacion-clase.html';


/**
 * Devuelve el topic vinculado a un módulo.
 *
 * Estrategia con fallback (orden de preferencia, primero gana):
 *   1) module.course_topic_id → query a course_topics (snapshot por curso)
 *   2) module.topic_id directo → query a template_topics por id
 *   3) Si module.topic_id es null → buscar template_topic con
 *      default_class_number = module.order_index + 1
 *
 * El paso (1) es lo que protege a cursos congelados (snapshot): el
 * Diplomado 09 lee de SU course_topics, aunque se actualice el template.
 * Los cursos nuevos sin snapshot todavía caen a (2) o (3).
 *
 * Devuelve siempre un TemplateTopic-compatible (course_topic tiene los
 * mismos campos relevantes, así el componente no cambia).
 */
export async function getTopicForModule(moduleId: string): Promise<TemplateTopic | null> {
  if (topicCache.has(moduleId)) return topicCache.get(moduleId)!;

  // Traemos topic_id + course_topic_id + order_index del módulo
  const { data: mod, error: e1 } = await supabase
    .from('modules')
    .select('topic_id, course_topic_id, order_index')
    .eq('id', moduleId)
    .maybeSingle();
  if (e1 || !mod) return null;

  let result: TemplateTopic | null = null;

  // Caso 1: course_topic_id (snapshot del curso — máxima prioridad)
  if (mod.course_topic_id) {
    const { data, error } = await supabase
      .from('course_topics')
      .select('*')
      .eq('id', mod.course_topic_id)
      .maybeSingle();
    if (error) {
      console.error('[recortes-html] getTopicForModule by course_topic_id:', error);
    } else if (data) {
      result = {
        id:                   data.id,
        template_id:          '',
        default_class_number: data.class_number ?? 0,
        name:                 data.name,
        description:          data.description ?? null,
        objective:            data.objective ?? null,
        duracion_h:           data.duracion_h ?? null,
        status:               data.status,
        html_ref:             data.html_ref ?? null,
        created_at:           data.created_at,
        updated_at:           data.updated_at,
      } as TemplateTopic;
    }
  }

  // Caso 2: topic_id directo → template_topics
  if (!result && mod.topic_id) {
    const { data, error } = await supabase
      .from('template_topics')
      .select('*')
      .eq('id', mod.topic_id)
      .maybeSingle();
    if (error) {
      console.error('[recortes-html] getTopicForModule by topic_id:', error);
    } else {
      result = data as TemplateTopic | null;
    }
  }

  // Caso 3: fallback por order_index → default_class_number en template_topics
  if (!result) {
    const classNumber = (mod.order_index ?? -1) + 1;
    if (classNumber >= 1) {
      const { data, error } = await supabase
        .from('template_topics')
        .select('*')
        .eq('default_class_number', classNumber)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('[recortes-html] getTopicForModule by class_number:', error);
      } else {
        result = data as TemplateTopic | null;
      }
    }
  }

  topicCache.set(moduleId, result);
  return result;
}


/**
 * Subtopics de un topic, ordenados.
 *
 * Si el topicId corresponde a un course_topic (tiene template_id === '' en
 * nuestro mapeo) → lee de course_subtopics. Sino → template_subtopics.
 *
 * Para no hacer dos queries, intentamos primero course_subtopics y si
 * no hay resultados caemos a template_subtopics. Es safe porque los IDs
 * son UUIDs únicos: nunca hay colisión entre course_topics y template_topics.
 */
export async function getSubtopicsForTopic(topicId: string): Promise<TemplateSubtopic[]> {
  // Intentar course_subtopics primero
  const { data: courseSubs, error: e1 } = await supabase
    .from('course_subtopics')
    .select('*')
    .eq('course_topic_id', topicId)
    .order('order_in_topic', { ascending: true });
  if (!e1 && courseSubs && courseSubs.length > 0) {
    // Mapear course_subtopic → TemplateSubtopic-compatible
    return courseSubs.map((s: any) => ({
      id:             s.id,
      topic_id:       s.course_topic_id,
      order_in_topic: s.order_in_topic,
      title:          s.title,
      section_label:  s.section_label ?? null,
      duracion_h:     s.duracion_h ?? null,
      status:         s.status,
      html_ref:       s.html_ref ?? null,
      created_at:     s.created_at,
    })) as TemplateSubtopic[];
  }

  // Sino: template_subtopics
  const { data, error } = await supabase
    .from('template_subtopics')
    .select('*')
    .eq('topic_id', topicId)
    .order('order_in_topic', { ascending: true });
  if (error) {
    console.error('[recortes-html] getSubtopicsForTopic:', error);
    return [];
  }
  return (data ?? []) as TemplateSubtopic[];
}


/**
 * Resuelve el html_ref efectivo de un topic.
 * - Si topic.html_ref está poblado → lo usa.
 * - Sino → busca fallback hardcodeado por default_class_number.
 * - Sino → cae a la plantilla universal (portada + índice generados).
 *
 * Nunca devuelve null — siempre hay algo para mostrar al alumno.
 */
export function resolveHtmlRef(topic: TemplateTopic): string {
  if (topic.html_ref) return topic.html_ref;
  return PLANTILLA_UNIVERSAL;
}


/** ¿La URL apunta a la plantilla universal (necesita render de placeholders)? */
export function isPlantillaUniversal(url: string): boolean {
  return url.endsWith(PLANTILLA_UNIVERSAL);
}


/**
 * Extrae la lista de `src` de slides del HTML de un recorte.
 * El index.html de cada recorte tiene:
 *   const slides = [{src: 'slides/slide-XX.html', label: '...'}, ...]
 * Devuelve los paths relativos (ej. 'slides/slide-03-intro.html').
 */
export function parseSlidesSrcs(html: string): string[] {
  const match = html.match(/const slides\s*=\s*\[([\s\S]*?)\];/);
  if (!match) return [];
  return [...match[1].matchAll(/src\s*:\s*['"]([^'"]+)['"]/g)].map(m => m[1]);
}


/** Datos que se inyectan en los placeholders del HTML. */
export interface PresentationContext {
  numClase?:        number | null;
  tituloClase?:     string;
  /** Subtemas del topic — el lib genera las cards a partir de esto. */
  subtemas?:        Array<{ order: number; title: string; description?: string | null; htmlRef?: string | null }>;
  docente?:         string | null;
  numeroDiplomado?: string | null;
  numeroUnidad?:    string | null;       // ej. "01"
  nombreUnidad?:    string | null;       // ej. "Fundamentos"
  totalClases?:     number | null;       // ej. 34
  duracionH?:       number | null;       // ej. 3
  subtitle?:        string | null;       // descripción larga
  /** Categoría del topic para el label de las cards. */
  categoria?:       string | null;
  /** Base URL para que las cards del índice abran el html_ref al hacer click. */
  htmlBase?:        string;
  /** URLs absolutas de los slides individuales (ya expandidos de los index.html de recortes). */
  recorteSlideUrls?: string[];
  /** Índice de slide destino para cada card clickable del índice (una por sub con html_ref). */
  cardOffsets?: number[];
}


/**
 * Reemplaza placeholders `{{KEY}}` en el HTML del recorte con valores reales.
 *
 * Placeholders soportados (todos opcionales — si el HTML no los tiene no pasa nada):
 *   PORTADA:
 *     {{NUMERO_UNIDAD}}     → "01"
 *     {{NOMBRE_UNIDAD}}     → "Fundamentos"
 *     {{NUM_CLASE}}         → "08"
 *     {{TOTAL_CLASES}}      → "34"
 *     {{TITULO_CLASE}}      → "Pisos y Cubiertas"
 *     {{SUBTITLE}}          → descripción larga
 *     {{DURACION_H}}        → "3"
 *     {{DURACION_MIN}}      → "180"
 *     {{DOCENTE}}           → "Mateo Ramírez"
 *   ÍNDICE:
 *     {{NUM_TEMAS}}         → "09" (zero-padded)
 *     {{INDICE_CARDS}}      → N divs .section-card pre-renderizados
 *     {{LISTA_TEMAS_CORTA}} → "SUELOS · CUBIERTAS"
 *   LEGACY (para HTMLs viejos como course-protocols):
 *     {{INDICE_TEMAS}}, {{INDICE_TEMAS_TXT}}, {{NUMERO_DIPLOMADO}}
 *
 * La regex acepta dígitos para que {{NUM_CLASE}} no se rompa.
 */
export function renderPresentationHtml(rawHtml: string, ctx: PresentationContext): string {
  const subs = ctx.subtemas ?? [];
  const cardsHtml = renderIndiceCards(subs, ctx.categoria ?? ctx.tituloClase ?? '', ctx.htmlBase);
  const indiceHtmlLegacy = subs.map((s) => `<li>${s.order}. ${escapeHtml(s.title)}</li>`).join('');
  const indiceTxt = subs.map((s) => `${s.order}. ${s.title}`).join(' · ');
  const listaCorta = subs.slice(0, 5).map((s) =>
    cleanTitle(s.title).toUpperCase().split(/\s+/).slice(0, 2).join(' ')
  ).join(' · ');
  const durH  = ctx.duracionH ?? 3;
  const durMin = Math.round(durH * 60);

  const nd = ctx.numeroDiplomado ?? '';
  const nombrePrograma = nd.toUpperCase().startsWith('CONCEPTO')
    ? `${nd.toUpperCase()} · BIM REVIT`
    : 'DIPLOMADO REVIT BIM';

  // Slides extra: un iframe por cada slide INDIVIDUAL de cada recorte.
  // Los slides individuales no tienen nav propia → una sola navegación.
  const recorteSlides = ctx.recorteSlideUrls?.length
    ? ctx.recorteSlideUrls.map(url =>
        `<div class="slide-canvas recorte-frame">` +
        `<iframe src="${url}" ` +
        `sandbox="allow-scripts allow-same-origin" ` +
        `style="width:1920px;height:1080px;border:0;display:block;" ` +
        `loading="eager"></iframe>` +
        `</div>`
      ).join('\n')
    : '';

  const values: Record<string, string> = {
    NOMBRE_PROGRAMA:   nombrePrograma,
    NUMERO_UNIDAD:     ctx.numeroUnidad ?? '01',
    NOMBRE_UNIDAD:     ctx.nombreUnidad ?? 'Fundamentos',
    NUM_CLASE:         ctx.numClase != null ? String(ctx.numClase).padStart(2, '0') : '—',
    TOTAL_CLASES:      ctx.totalClases != null ? String(ctx.totalClases) : '34',
    TITULO_CLASE:      ctx.tituloClase ?? '',
    SUBTITLE:          ctx.subtitle ?? defaultSubtitle(ctx.tituloClase ?? ''),
    DURACION_H:        String(durH),
    DURACION_MIN:      String(durMin),
    DOCENTE:           ctx.docente ?? 'Por definir',
    NUM_TEMAS:         String(subs.length).padStart(2, '0'),
    INDICE_CARDS:      cardsHtml,
    LISTA_TEMAS_CORTA: listaCorta,
    RECORTE_SLIDES:    recorteSlides,
    // Legacy (protocol BIM):
    NUMERO_DIPLOMADO:  ctx.numeroDiplomado ?? '',
    INDICE_TEMAS:      indiceHtmlLegacy ? `<ol>${indiceHtmlLegacy}</ol>` : '',
    INDICE_TEMAS_TXT:  indiceTxt,
  };

  let result = rawHtml.replace(/\{\{([A-Z0-9_]+)\}\}/g, (_, key) => values[key] ?? '');

  // Script inyectado: click en card del índice → navega al primer slide del recorte.
  // cardOffsets[i] = índice del slide correspondiente a la i-ésima card clickable.
  if (ctx.cardOffsets && ctx.cardOffsets.length > 0) {
    const script = `<script>(function(){var o=[${ctx.cardOffsets.join(',')}];document.querySelectorAll('[data-open-url]').forEach(function(el,i){el.addEventListener('click',function(){if(window._cubusShow&&o[i]!=null)window._cubusShow(o[i]);});});})();<\/script>`;
    result = result.replace('</body>', script + '\n</body>');
  }

  return result;
}


/**
 * Genera el HTML de las N cards .section-card del índice a partir de los
 * subtemas del topic. La plantilla universal usa esto en {{INDICE_CARDS}}.
 * El grid se adapta automáticamente al N (data-n se setea via JS en la plantilla).
 *
 * Si htmlBase y s.htmlRef están presentes, la card recibe data-open-url
 * para que el script inyectado la abra al hacer click.
 */
export function renderIndiceCards(
  subs: Array<{ order: number; title: string; description?: string | null; htmlRef?: string | null }>,
  defaultLabel: string,
  htmlBase?: string,
): string {
  return subs.map((s, i) => {
    const num     = String(i + 1).padStart(2, '0');
    const label   = (defaultLabel || 'TEMA').toUpperCase().slice(0, 20);
    const name    = cleanTitle(s.title);
    const desc    = s.description || '';
    // Si el ref no tiene extensión es una carpeta → agregar /index.html para Storage prod
    const ref = s.htmlRef
      ? (s.htmlRef.includes('.') ? s.htmlRef : `${s.htmlRef}/index.html`)
      : null;
    const openUrl = (htmlBase && ref) ? `${htmlBase}/${ref}` : null;
    const attrs   = openUrl
      ? ` data-open-url="${escapeHtml(openUrl)}" style="cursor:pointer"`
      : '';
    return `<div class="section-card"${attrs}>` +
      `<div class="badge">Sección</div>` +
      `<div class="num">${num}</div>` +
      `<div class="label">// ${escapeHtml(label)}</div>` +
      `<div class="name">${escapeHtml(name)}</div>` +
      (desc ? `<div class="desc">${escapeHtml(desc)}</div>` : '') +
      `</div>`;
  }).join('');
}


/** Quita prefijos tipo "1. " o "12) " del título del subtema. */
function cleanTitle(s: string): string {
  return String(s).replace(/^\s*\d+\s*[\.\)\-]?\s*/, '').trim();
}


/** Subtitle de fallback cuando topic no tiene descripción. */
function defaultSubtitle(titulo: string): string {
  if (!titulo) return '';
  return `Recorrido completo por ${titulo.toLowerCase()} — conceptos, ejemplos y aplicación práctica.`;
}


function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}


/**
 * Construye la URL pública del HTML del topic.
 * Devuelve null si no hay html_ref y no hay fallback.
 */
export function buildPresentationUrl(topic: TemplateTopic): string | null {
  const ref = resolveHtmlRef(topic);
  if (!ref) return null;
  return `${RECORTES_HTML_BASE}/${ref}`;
}
